import { useState } from 'react';
import { Link } from 'react-router-dom';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { ArrowRightIcon, CheckIcon } from 'lucide-react';
import { FormError } from '../shared/FormError';
import { Spinner } from '../../ui/Spinner';
import { inlineHit } from '../../ui/styles';
import { useSubmit } from '../../hooks/useSubmit';
import { demoNewsletterLink, newRequestId, subscribeNewsletter } from '../../utils/api';
import { legalLinks } from '../../data/legal';
import { founder } from '../../data/content';
import { newsletterConsent } from '../../data/consent';
import { ease } from '../../core/format/motion';
import { track } from '../../utils/analytics';

/**
 * Nieuwsbrief bovenaan de footer (op klei): één veld, double opt-in, en een duidelijke
 * bevestiging. Alle tekst in shell (5,6:1 op klei); het accent in blush alleen groot.
 */
export function NewsletterSignup() {
  const [email, setEmail] = useState('');
  const signup = useSubmit(subscribeNewsletter);
  const reduce = useReducedMotion();
  return <div className="grid gap-10 py-16 lg:grid-cols-12 lg:items-end lg:gap-16 lg:py-24" data-id="element-7889" data-mp="3bqcql0qi3dti">
      <div className="lg:col-span-6" data-id="element-7890" data-mp="3gjxlul0pltp0">
        <h2 id="newsletter-heading" className="font-display text-[2.4rem] font-light leading-[1.05] tracking-[-0.015em] text-shell sm:text-[3rem]" data-id="element-7891" data-mp="32qy226tw9dbn">
          De vijf fouten die ik
          <span className="italic text-blush" data-id="element-7892" data-mp="37nok2zp9eizp"> het vaakst zie.</span>
        </h2>
        <p className="mt-5 max-w-md text-[16px] leading-relaxed text-shell" data-id="element-7893" data-mp="3vgesiyxyek58">
          Meld je aan en je krijgt ze meteen in je inbox. Plus één keer per maand wat {founder.firstName} in de salon opviel. Geen aanbiedingenspam.
        </p>
      </div>
      {/* De bevestiging heeft zelf role="status"; geen extra live-regio nodig. */}
      <div className="lg:col-span-6" data-id="element-7894" data-mp="2a2af86thyfvz">
        <AnimatePresence mode="wait" initial={false} data-id="element-7895" data-mp="2a1909r9d198z">
          {signup.succeeded ? <motion.p key="done" initial={reduce ? {
          opacity: 0
        } : {
          opacity: 0,
          y: 6
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.2,
          ease
        }} role="status" className="flex items-center gap-3 text-[16px] text-shell" data-id="element-7896" data-mp="2f1na9euiut9n">
              <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-shell text-clay" data-id="element-7897" data-mp="1m5e10py29oxl">
                <CheckIcon className="h-4 w-4" strokeWidth={2} aria-hidden="true" data-id="element-7898" data-mp="1h5rpqm1smirf" />
              </span>
              <span data-id="element-7899" data-mp="1c6esr8h7jvb4">
                Bijna klaar: bevestig je aanmelding via de mail naar {email.trim()}.
                {/* DEMO: met een backend is demoNewsletterLink null en verdwijnt deze knop vanzelf. */}
                {demoNewsletterLink && <Link to={demoNewsletterLink} className="mt-2 block text-[14px] text-shell underline underline-offset-4" data-id="element-7900" data-mp="ya1mkvi4jgby">
                    Open de bevestigingslink (demo)
                  </Link>}
              </span>
            </motion.p> : <motion.form key="form" exit={{
          opacity: 0
        }} transition={{
          duration: 0.15,
          ease
        }} aria-labelledby="newsletter-heading" aria-busy={signup.submitting} onSubmit={e => {
          e.preventDefault();
          if (signup.submitting) return;
          void signup.submit({
            requestId: newRequestId(),
            email: email.trim(),
            source: 'footer',
            consentVersion: newsletterConsent.version
          }).then(ok => {
            if (ok) track('nieuwsbrief-aangemeld', {
              bron: 'footer'
            });
          });
        }} data-id="element-7901" data-mp="30722vghkrksr">
              <div className="flex flex-col gap-4 sm:flex-row sm:items-end" data-id="element-7902" data-mp="2v7h5hai1zki5">
                <label htmlFor="footer-email" className="sr-only" data-id="element-7903" data-mp="2v5coprz22r0g">
                  E-mailadres
                </label>
                <input id="footer-email" type="email" required autoComplete="email" inputMode="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="jouw@email.nl" aria-invalid={signup.error ? true : undefined} aria-describedby={signup.error ? 'footer-email-error footer-email-hint' : 'footer-email-hint'} className="w-full border-b border-shell/50 bg-transparent px-1 py-3 text-[17px] text-shell placeholder:text-shell/80 transition-colors duration-150 ease-soft focus:border-shell focus:outline-none" data-id="element-7904" data-mp="ah8576shqs6o" />
                <button type="submit" disabled={signup.submitting} className="group flex min-h-[52px] items-center justify-center gap-2 whitespace-nowrap rounded-full bg-shell px-7 text-[15px] text-ink transition-[background-color,transform] duration-150 ease-soft hover:bg-blush active:scale-[0.98] disabled:cursor-wait disabled:opacity-80" data-id="element-7905" data-mp="11noz7fkqgsi9">
                  {signup.submitting ? <Spinner data-id="element-7906" data-mp="1pa0vfpwrrnbd" /> : null}
                  Stuur me de vijf fouten
                  {!signup.submitting && <ArrowRightIcon className="h-4 w-4 transition-transform duration-200 ease-soft group-hover:translate-x-0.5" strokeWidth={1.5} aria-hidden="true" data-id="element-7907" data-mp="1gbp9i16uldp9" />}
                </button>
              </div>
              <p id="footer-email-hint" className="mt-4 text-[12px] leading-relaxed text-shell" data-id="element-7908" data-mp="16kkux2l33uuy">
                {newsletterConsent.text} Lees ons{' '}
                <Link to={legalLinks.privacy} className={`${inlineHit} underline underline-offset-2`} data-id="element-7909" data-mp="1be6ze7h3gsfo">
                  privacybeleid
                </Link>
                .
              </p>
              <FormError id="footer-email-error" message={signup.error} className="mt-4" data-id="element-7910" data-mp="11kg9cy5bk9xv" />
            </motion.form>}
        </AnimatePresence>
      </div>
    </div>;
}